import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  FaBus, FaArrowRight, FaPhoneAlt, FaEnvelope, 
  FaTicketAlt, FaShieldAlt, FaWallet, FaCreditCard, FaPlus 
} from "react-icons/fa";
import { baseurl } from "../../baseurl";

const ConfirmBooking = () => {
  const { state } = useLocation();
  const navigate = useNavigate();

  const [walletBalance, setWalletBalance] = useState(0);
  const [useWallet, setUseWallet] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const userId = localStorage.getItem("guestUserId");

  useEffect(() => {
    if (userId) fetchWalletBalance();
  }, [userId]);

  const fetchWalletBalance = async () => {
    try {
      const res = await axios.get(`${baseurl}/getWalletBalance/${userId}`);
      setWalletBalance(parseFloat(res.data.balance) || 0);
    } catch (err) {
      console.error("Error fetching wallet balance", err);
    }
  };

  if (!state) return null;

  // --- EXTRACT MARKUP DATA FROM STATE ---
  const { 
    bookingDetails, 
    email, 
    phone, 
    totalFare, 
    boardingName, 
    droppingName,
    appliedMarkupType,
    appliedMarkupAmount 
  } = state;

  const blockKey = bookingDetails?.data?.blockKey;

  // --- CONSOLE LOG FOR VERIFICATION ---
  console.log("--- Payment Summary Breakdown ---");
  console.log("Total Fare (Visible to User):", totalFare);
  console.log("Markup Applied:", appliedMarkupAmount, `(${appliedMarkupType})`);
  console.log("---------------------------------");

  // --- CALCULATIONS WITH GATEWAY CHARGES ---
  const GATEWAY_CHARGE_PERCENT = 0.027; // 2.7%

  const appliedWalletAmount = useWallet ? Math.min(walletBalance, totalFare) : 0;
  const baseGatewayAmount = totalFare - appliedWalletAmount;
  const platformFee = baseGatewayAmount > 0 ? Math.round(baseGatewayAmount * GATEWAY_CHARGE_PERCENT) : 0;
  const finalGatewayAmount = Math.round(baseGatewayAmount + platformFee);
  const netPayable = baseGatewayAmount === 0 ? totalFare : finalGatewayAmount;

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleFinalPayment = async () => {
    setIsProcessing(true);

    const isLoaded = await loadRazorpayScript();
    if (!isLoaded) {
      alert("Razorpay SDK failed to load.");
      setIsProcessing(false);
      return;
    }

    try {
      // --- CASE 1: Full Wallet Payment ---
      if (useWallet && baseGatewayAmount === 0) {
        const res = await axios.post(`${baseurl}/payViaWalletOnly`, {
          userId,
          blockKey,
          amount: totalFare,
          // Sending markup data here
          appliedMarkupType,
          appliedMarkupAmount
        });

        if (res.data.status === 'success') {
          navigate("/booking-success", { state: { ticket: res.data.ticket } });
        }
        return;
      }

      // --- CASE 2: Gateway or Split Payment ---
      const orderRes = await axios.post(`${baseurl}/createBusOrder`, {
        amount: finalGatewayAmount,
        userId,
        blockKey,
        walletUsed: appliedWalletAmount,
        platformFee: platformFee,
        // Sending markup data to backend
        appliedMarkupType,
        appliedMarkupAmount
      });

      console.log("orderRes", orderRes);

      const options = {
        key: "rzp_live_bb0TqJFLwrGXrs", 
        amount: orderRes.data.amount,
        currency: "INR",
        name: "Bus Booking",
        description: appliedWalletAmount > 0 ? `Split Pay: Wallet + Online` : `Online Payment`,
        order_id: orderRes.data.id,
        handler: async function (response) {
          const verificationData = {
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_order_id: response.razorpay_order_id,
            razorpay_signature: response.razorpay_signature,
            blockKey,
            userId,
            walletUsed: appliedWalletAmount,
            amountPaid: finalGatewayAmount,
            // Including markup in verification for final calculations
            appliedMarkupType,
            appliedMarkupAmount
          };

          const verifyRes = await axios.post(`${baseurl}/verifyBusPayment`, verificationData);
          
           console.log("verifyRes", verifyRes);


          if (verifyRes.data.status === 'success') {
            navigate("/booking", { state: { ticket: verifyRes.data.ticket } });
          } else {
            alert("Payment verification failed.");
            setIsProcessing(false);
          }
        },
        prefill: { email, contact: phone },
        theme: { color: "#dc3545" },
        modal: {
          ondismiss: () => setIsProcessing(false)
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();

    } catch (err) {
      console.error("Payment Error:", err);
      alert(err.response?.data?.message || "Payment failed.");
      setIsProcessing(false);
    }
  };

  return (
    <div className="bg-light min-vh-100 py-4">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-9">
            <h4 className="fw-bold mb-4">Finalize Your Journey</h4>

            <div className="row g-4">
              <div className="col-md-8">
                <div className="card border-0 shadow-sm mb-4 p-4">
                   <div className="d-flex justify-content-between align-items-center border-bottom pb-3 mb-3">
                      <h6 className="fw-bold m-0 text-muted">Ticket Details</h6>
                      <span className="small text-success fw-bold">ID: {blockKey}</span>
                   </div>
                   <div className="row text-center align-items-center">
                      <div className="col-5">
                        <small className="text-muted d-block text-uppercase">From</small>
                        <span className="fw-bold">{boardingName}</span>
                      </div>
                      <div className="col-2"><FaArrowRight className="text-danger" /></div>
                      <div className="col-5">
                        <small className="text-muted d-block text-uppercase">To</small>
                        <span className="fw-bold">{droppingName}</span>
                      </div>
                   </div>
                </div>

                <div className={`card border-0 shadow-sm mb-3 ${useWallet ? 'border border-success' : ''}`}>
                  <div className="card-body d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                      <div className={`p-3 rounded-circle me-3 ${walletBalance > 0 ? 'bg-success-soft text-success' : 'bg-light text-muted'}`}>
                        <FaWallet size={20} />
                      </div>
                      <div>
                        <h6 className="fw-bold mb-0">Apply Wallet Balance</h6>
                        <small className="text-muted">Available: ₹{walletBalance}</small>
                      </div>
                    </div>
                    <div className="form-check form-switch">
                      <input 
                        className="form-check-input" 
                        type="checkbox" 
                        disabled={walletBalance === 0}
                        checked={useWallet}
                        onChange={() => setUseWallet(!useWallet)}
                        style={{ width: '45px', height: '22px', cursor: 'pointer' }}
                      />
                    </div>
                  </div>
                </div>

                {baseGatewayAmount > 0 && (
                  <div className="card border-0 shadow-sm p-3 bg-white">
                    <div className="d-flex align-items-center">
                      <FaCreditCard className="text-danger me-3 fs-4" />
                      <div>
                        <h6 className="fw-bold mb-0">Online Payment</h6>
                        <small className="text-muted">Pay ₹{finalGatewayAmount} (Includes ₹{platformFee} platform fee)</small>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="col-md-4">
                <div className="card border-0 shadow-sm sticky- " style={{ top: "20px" }}>
                  <div className="card-header bg-white py-3"><h6 className="fw-bold m-0">Payment Summary</h6></div>
                  <div className="card-body">
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Ticket Fare</span>
                      <span>₹{totalFare}</span>
                    </div>

                    {useWallet && appliedWalletAmount > 0 && (
                      <div className="d-flex justify-content-between mb-2 text-success fw-bold">
                        <span>Wallet Applied</span>
                        <span>- ₹{appliedWalletAmount}</span>
                      </div>
                    )}

                    {platformFee > 0 && (
                      <div className="d-flex justify-content-between mb-2">
                        <span className="text-muted">Platform Fee (2.7%)</span>
                        <span>₹{platformFee}</span>
                      </div>
                    )}

                    <hr />
                    
                    <div className="d-flex justify-content-between mb-4">
                      <h5 className="fw-bold">Net Payable</h5>
                      <h5 className="fw-bold text-danger">₹{netPayable}</h5>
                    </div>

                    <button 
                      className="btn btn-danger w-100 py-3 fw-bold mb-3 shadow"
                      onClick={handleFinalPayment}
                      disabled={isProcessing}
                    >
                      {isProcessing ? "Processing..." : baseGatewayAmount === 0 ? "PAY USING WALLET" : `PROCEED TO PAY ₹${finalGatewayAmount}`}
                    </button>

                    <div className="text-center">
                        <small className="text-muted"><FaShieldAlt /> 100% Secure Transaction</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmBooking;